import { Navigate, useLocation } from "react-router-dom"
import type { ReactNode } from "react"
import { useAuthStore } from "@/stores/auth-store"

type StaffRole = "advisor" | "operator" | "admin"

const ROLE_RANK: Record<StaffRole, number> = {
  advisor: 1,
  operator: 2,
  admin: 3,
}

interface RequireAuthProps {
  children: ReactNode
  minRole?: StaffRole
}

/**
 * Route guard — 未登录 → /login（保留来源 path 在 location.state）。
 * minRole 指定时还会检查角色等级（advisor < operator < admin）。
 */
export function RequireAuth({ children, minRole = "advisor" }: RequireAuthProps) {
  const location = useLocation()
  const accessToken = useAuthStore((s) => s.accessToken)
  const staff = useAuthStore((s) => s.staff)

  if (!accessToken || !staff) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }
  const actualRank = ROLE_RANK[staff.role] ?? 0
  const requiredRank = ROLE_RANK[minRole]
  if (actualRank < requiredRank) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8 text-center text-muted-foreground">
        <div>
          <h2 className="text-xl font-semibold mb-2">权限不足</h2>
          <p className="text-sm">
            该页面需要 <span className="font-medium">{minRole}</span> 及以上权限，
            你的当前角色是 <span className="font-medium">{staff.role}</span>。
          </p>
        </div>
      </div>
    )
  }
  return <>{children}</>
}

/**
 * 反向 guard — 已登录访问 /login 时 → /dashboard
 */
export function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const accessToken = useAuthStore((s) => s.accessToken)
  if (accessToken) {
    return <Navigate to="/dashboard" replace />
  }
  return <>{children}</>
}

/**
 * must_change_password 强制拦截 — 必须先去 /profile 改密才能用其它功能。
 * 套在 AppShell 内层，让 /profile 自身可访问。
 */
export function MustChangePasswordGuard({ children }: { children: ReactNode }) {
  const location = useLocation()
  const staff = useAuthStore((s) => s.staff)

  if (staff?.must_change_password && location.pathname !== "/profile") {
    return <Navigate to="/profile" state={{ from: location }} replace />
  }
  return <>{children}</>
}
